import React, { useState, useEffect, useRef } from 'react';
import AiErrorBoundary from '../components/AiErrorBoundary';
import { inboxFetch } from '../lib/inboxApi';
import { colors } from '../theme/tokens';

// ── Toggle switch shared component ───────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <div style={s.toggleRow}>
      <span style={s.toggleLabel}>{label}</span>
      <button
        style={{ ...s.toggle, ...(value ? s.toggleOn : {}) }}
        onClick={() => onChange(!value)}
      >
        <span style={{ ...s.togglePill, ...(value ? s.togglePillOn : {}) }}>
          <span style={{ ...s.toggleThumb, ...(value ? s.toggleThumbOn : {}) }} />
        </span>
        <span style={s.toggleText}>{value ? 'Enabled' : 'Disabled'}</span>
      </button>
    </div>
  );
}

// ── Settings Tab ──────────────────────────────────────────────────────────────
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    inboxFetch(`/api/ai/settings`)
      .then(r => r.json())
      .then(data => setSettings(data))
      .catch(() => setError('Could not load AI settings.'));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await inboxFetch(`/api/ai/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (!res.ok) throw new Error('Save failed');
      const updated = await res.json();
      setSettings(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Could not save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div style={s.loading}>Loading…</div>;

  return (
    <div style={s.tabContent}>
      <p style={s.description}>
        When enabled, the AI suggests a reply draft above the message input whenever a customer
        sends a message. Agents can use, edit, or dismiss the suggestion.
      </p>

      {error && <div style={s.error}>{error}</div>}

      <Toggle
        label="AI product suggestions"
        value={settings.enabled}
        onChange={val => setSettings(p => ({ ...p, enabled: val }))}
      />

      <div style={s.field}>
        <label style={s.fieldLabel}>Store search URL base</label>
        <input
          style={s.input}
          value={settings.searchBaseUrl}
          onChange={e => setSettings(p => ({ ...p, searchBaseUrl: e.target.value }))}
          placeholder="https://parmegastore.com/search?type=product&q="
        />
        <div style={s.fieldHint}>
          The SKU is appended to this URL to create the product link.
          Example result: <code style={s.code}>{settings.searchBaseUrl}PR310</code>
        </div>
      </div>

      <div style={s.field}>
        <label style={s.fieldLabel}>Tone &amp; response rules</label>
        <textarea
          style={s.textarea}
          rows={8}
          value={settings.toneRules}
          onChange={e => setSettings(p => ({ ...p, toneRules: e.target.value }))}
          placeholder="Enter instructions for how the AI should respond to customers…"
        />
        <div style={s.fieldHint}>
          These rules control the AI's tone, language, and behaviour. They also include example
          "priming" questions — the clarifying questions the AI asks when a customer has not given
          enough detail to find a product match (e.g. greeting-only, URL, image, or vague request).
        </div>
      </div>

      <div style={s.footer}>
        <button
          style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : saved ? '✓ Saved' : 'Save Settings'}
        </button>
        {saved && <span style={s.savedMsg}>Settings saved successfully.</span>}
      </div>
    </div>
  );
}

// ── Collections Tab ───────────────────────────────────────────────────────────
function CollectionsTab() {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadMsg, setUploadMsg] = useState(null);
  const fileRef = useRef(null);

  const fetchCollections = () => {
    setLoading(true);
    inboxFetch(`/api/ai/collections`)
      .then(r => r.json())
      .then(data => { setCollections(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => { setError('Could not load collections.'); setLoading(false); });
  };

  useEffect(() => { fetchCollections(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg(null);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await inboxFetch(`/api/ai/collections`, {
        method: 'POST',
        body: form
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      setUploadMsg(data.message);
      fetchCollections();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection? This cannot be undone.')) return;
    try {
      await inboxFetch(`/api/ai/collections/${id}`, { method: 'DELETE' });
      setCollections(prev => prev.filter(c => c._id !== id));
    } catch {
      setError('Could not delete collection.');
    }
  };

  // Group by collectionName for display
  const grouped = collections.reduce((acc, c) => {
    const key = c.collectionName;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  return (
    <div style={s.tabContent}>
      <p style={s.description}>
        Upload CSV files containing your product catalog. Each CSV can have multiple categories.
        The AI will search across all uploaded collections when suggesting products.
      </p>

      <div style={s.csvSpec}>
        <div style={s.csvSpecTitle}>Required CSV columns:</div>
        <code style={s.csvCode}>category, sku, name, description</code>
        <div style={s.csvSpecExample}>
          Example row: <code style={s.code}>Door Seals, PR310, Universal Door Seal, D-profile seal with fender lip</code>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}
      {uploadMsg && <div style={s.success}>{uploadMsg}</div>}

      <input
        ref={fileRef}
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      <button
        style={{ ...s.uploadBtn, ...(uploading ? s.saveBtnDisabled : {}) }}
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? 'Uploading…' : '+ Upload CSV'}
      </button>

      {loading && <div style={s.loading}>Loading collections…</div>}

      {!loading && Object.keys(grouped).length === 0 && (
        <div style={s.emptyCollections}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>📂</div>
          <div>No product collections uploaded yet.</div>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
            Upload a CSV to enable AI product recommendations.
          </div>
        </div>
      )}

      <div style={s.collectionList}>
        {Object.entries(grouped).map(([name, cats]) => {
          const totalProducts = cats.reduce((n, c) => n + c.products.length, 0);
          return (
            <div key={name} style={s.collectionCard}>
              <div style={s.collectionCardHeader}>
                <div>
                  <div style={s.collectionName}>{name}</div>
                  <div style={s.collectionMeta}>
                    {cats.length} {cats.length === 1 ? 'category' : 'categories'} &middot; {totalProducts} products
                  </div>
                </div>
                <button
                  style={s.deleteBtn}
                  onClick={() => cats.forEach(c => handleDelete(c._id))}
                  title="Delete all categories in this collection"
                >
                  Delete
                </button>
              </div>
              <div style={s.categoryTags}>
                {cats.map(c => (
                  <span key={c._id} style={s.categoryTag}>{c.category} ({c.products.length})</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Enrichments Tab ───────────────────────────────────────────────────────────
function EnrichmentsTab({ onPendingCountChange }) {
  const [enrichments, setEnrichments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [analyzeMsg, setAnalyzeMsg] = useState(null);

  const fetchEnrichments = (status = filter) => {
    setLoading(true);
    const url = status === 'all'
      ? `/api/ai/enrichments`
      : `/api/ai/enrichments?status=${status}`;
    inboxFetch(url)
      .then(r => r.json())
      .then(data => {
        setEnrichments(Array.isArray(data.enrichments) ? data.enrichments : []);
        if (typeof data.pendingCount === 'number') {
          onPendingCountChange?.(data.pendingCount);
        }
        setLoading(false);
      })
      .catch(() => { setError('Could not load enrichments.'); setLoading(false); });
  };

  useEffect(() => { fetchEnrichments(); }, [filter]); // eslint-disable-line

  const handleAnalyze = async () => {
    setAnalyzing(true);
    setAnalyzeMsg(null);
    setError(null);
    try {
      const res = await inboxFetch(`/api/ai/analyze-resolved`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Analysis failed');
      setAnalyzeMsg(data.message + ' — results will appear below as they complete.');
      setTimeout(() => fetchEnrichments('pending'), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDecision = async (id, status) => {
    try {
      const res = await inboxFetch(`/api/ai/enrichments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setEnrichments(prev => prev.filter(e => e._id !== id));
      if (typeof data.pendingCount === 'number') {
        onPendingCountChange?.(data.pendingCount);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={s.tabContent}>
      <p style={s.description}>
        After every resolved conversation, the AI checks if any new product fitment was confirmed
        (e.g. "PR310 fits Audi A3 boot seal"). Suggestions appear here for your review.
        Approved suggestions are automatically added to the product description, improving future recommendations.
      </p>

      <div style={s.enrichToolbar}>
        <div style={s.filterTabs}>
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button
              key={f}
              style={{ ...s.filterTab, ...(filter === f ? s.filterTabActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <button
          style={{ ...s.analyzeBtn, ...(analyzing ? s.saveBtnDisabled : {}) }}
          onClick={handleAnalyze}
          disabled={analyzing}
          title="Scan last 50 resolved conversations for new product fitment info"
        >
          {analyzing ? 'Analyzing…' : 'Analyze resolved chats'}
        </button>
      </div>

      {error && <div style={s.error}>{error}</div>}
      {analyzeMsg && <div style={s.success}>{analyzeMsg}</div>}

      {loading && <div style={s.loading}>Loading…</div>}

      {!loading && enrichments.length === 0 && (
        <div style={s.emptyCollections}>
          <div style={{ fontSize: '28px', marginBottom: '8px' }}>🔍</div>
          <div>No {filter === 'all' ? '' : filter} enrichments found.</div>
          {filter === 'pending' && (
            <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '4px' }}>
              Enrichments appear automatically when conversations are resolved,
              or click "Analyze resolved chats" to scan existing ones.
            </div>
          )}
        </div>
      )}

      <div style={s.enrichList}>
        {enrichments.map(e => (
          <div key={e._id} style={{
            ...s.enrichCard,
            ...(e.status === 'approved' ? s.enrichCardApproved : {}),
            ...(e.status === 'rejected' ? s.enrichCardRejected : {})
          }}>
            <div style={s.enrichHeader}>
              <span style={s.enrichSku}>SKU: {e.productSku}</span>
              <span style={{
                ...s.enrichStatusBadge,
                ...(e.status === 'approved' ? s.badgeApproved
                  : e.status === 'rejected' ? s.badgeRejected
                  : s.badgePending)
              }}>
                {e.status}
              </span>
            </div>

            {e.currentDescription && (
              <div style={s.enrichCurrent}>
                <span style={s.enrichSubLabel}>Current description:</span> {e.currentDescription}
              </div>
            )}

            <div style={s.enrichSuggestion}>
              <span style={s.enrichSubLabel}>Suggested addition:</span>{' '}
              <strong>{e.suggestedAddition}</strong>
            </div>

            {e.evidence && (
              <div style={s.enrichEvidence}>
                <span style={s.enrichSubLabel}>Evidence:</span> {e.evidence}
              </div>
            )}

            {e.status === 'pending' && (
              <div style={s.enrichActions}>
                <button
                  style={s.approveBtn}
                  onClick={() => handleDecision(e._id, 'approved')}
                >
                  Approve
                </button>
                <button
                  style={s.rejectBtn}
                  onClick={() => handleDecision(e._id, 'rejected')}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main InboxAiSettings ──────────────────────────────────────────────────────
export default function InboxAiSettings({ onPendingCountChange }) {
  const [activeTab, setActiveTab] = useState('settings');
  const [pendingCount, setPendingCount] = useState(0);

  const handlePendingCountChange = (count) => {
    setPendingCount(count);
    onPendingCountChange?.(count);
  };

  return (
    <AiErrorBoundary>
      <div style={s.container}>
        <div style={s.tabs}>
          {[
            { id: 'settings', label: 'Settings' },
            { id: 'collections', label: 'Collections' },
            {
              id: 'enrichments',
              label: pendingCount > 0
                ? `Enrichments (${pendingCount})`
                : 'Enrichments'
            }
          ].map(tab => (
            <button
              key={tab.id}
              style={{ ...s.tab, ...(activeTab === tab.id ? s.tabActive : {}) }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'settings' && <SettingsTab />}
        {activeTab === 'collections' && <CollectionsTab />}
        {activeTab === 'enrichments' && (
          <EnrichmentsTab onPendingCountChange={handlePendingCountChange} />
        )}
      </div>
    </AiErrorBoundary>
  );
}

// ── Styles (dark mode) ────────────────────────────────────────────────────────
const s = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  tabs: {
    display: 'flex',
    borderBottom: `1px solid ${colors.glassBorder}`,
    background: colors.surfaceSolid,
    padding: '0 16px',
    flexShrink: 0
  },
  tab: {
    padding: '10px 16px',
    border: 'none',
    background: 'transparent',
    color: colors.textMuted,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    borderBottom: '2px solid transparent',
    marginBottom: '-1px',
    transition: 'all 0.15s'
  },
  tabActive: {
    color: colors.text,
    borderBottomColor: '#2fb65c'
  },
  tabContent: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1
  },
  description: {
    fontSize: '13px',
    color: colors.textMuted,
    marginBottom: '20px',
    lineHeight: '1.6',
    marginTop: 0
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: colors.textMuted,
    fontSize: '13px'
  },
  error: {
    background: 'rgba(229,84,75,0.14)',
    color: '#f3b0aa',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '14px',
    border: '1px solid rgba(229,84,75,0.35)'
  },
  success: {
    background: 'rgba(47,182,92,0.14)',
    color: colors.parGreenBright,
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '14px',
    border: '1px solid rgba(47,182,92,0.35)'
  },
  // Toggle
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: colors.glass,
    borderRadius: '10px',
    border: `1px solid ${colors.glassBorder}`,
    marginBottom: '20px'
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: '700',
    color: colors.text
  },
  toggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '20px',
    background: colors.surfaceDeep,
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: colors.textMuted,
    transition: 'all 0.2s'
  },
  toggleOn: {
    background: 'rgba(47,182,92,0.18)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: colors.parGreenBright
  },
  togglePill: {
    width: '36px',
    height: '20px',
    background: colors.borderStrong,
    borderRadius: '10px',
    position: 'relative',
    flexShrink: 0,
    transition: 'background 0.2s'
  },
  togglePillOn: { background: '#2fb65c' },
  toggleThumb: {
    position: 'absolute',
    top: '2px',
    left: '2px',
    width: '16px',
    height: '16px',
    background: 'white',
    borderRadius: '50%',
    transition: 'left 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
  },
  toggleThumbOn: { left: '18px' },
  toggleText: { userSelect: 'none', minWidth: '56px' },
  // Form fields
  field: { marginBottom: '18px' },
  fieldLabel: {
    display: 'block',
    fontSize: '11px',
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px'
  },
  fieldHint: {
    fontSize: '11px',
    color: colors.textFaint,
    marginTop: '5px',
    lineHeight: '1.5'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: colors.inputBg,
    color: colors.text
  },
  textarea: {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    lineHeight: '1.6',
    resize: 'vertical',
    background: colors.inputBg,
    color: colors.text
  },
  code: {
    background: colors.surface2,
    padding: '1px 5px',
    borderRadius: '3px',
    fontSize: '11px',
    fontFamily: 'monospace',
    color: colors.text
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    marginTop: '8px'
  },
  saveBtn: {
    background: colors.parGreen,
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 28px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  saveBtnDisabled: {
    background: colors.surface2,
    color: colors.textFaint,
    cursor: 'not-allowed'
  },
  savedMsg: {
    color: colors.parGreenBright,
    fontSize: '13px',
    fontWeight: '600'
  },
  // Collections
  csvSpec: {
    background: colors.surfaceSoft,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '16px'
  },
  csvSpecTitle: {
    fontSize: '12px',
    fontWeight: '700',
    color: colors.text,
    marginBottom: '4px'
  },
  csvCode: {
    display: 'block',
    fontFamily: 'monospace',
    fontSize: '12px',
    color: colors.blue,
    marginBottom: '6px'
  },
  csvSpecExample: {
    fontSize: '11px',
    color: colors.textMuted
  },
  uploadBtn: {
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '16px',
    display: 'inline-block'
  },
  emptyCollections: {
    textAlign: 'center',
    padding: '32px',
    color: colors.textMuted,
    fontSize: '13px'
  },
  collectionList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  collectionCard: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '14px 16px'
  },
  collectionCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '8px'
  },
  collectionName: {
    fontWeight: '700',
    fontSize: '14px',
    color: colors.text
  },
  collectionMeta: {
    fontSize: '11px',
    color: colors.textMuted,
    marginTop: '2px'
  },
  deleteBtn: {
    background: 'transparent',
    border: '1px solid rgba(229,84,75,0.4)',
    color: '#f3b0aa',
    borderRadius: '5px',
    padding: '3px 10px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },
  categoryTags: { display: 'flex', flexWrap: 'wrap', gap: '5px' },
  categoryTag: {
    background: 'rgba(58,155,212,0.18)',
    color: '#8fcdee',
    borderRadius: '10px',
    padding: '2px 9px',
    fontSize: '11px',
    fontWeight: '600'
  },
  // Enrichments
  enrichToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px'
  },
  filterTabs: { display: 'flex', gap: '4px' },
  filterTab: {
    padding: '5px 12px',
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '12px',
    background: colors.surfaceDeep,
    color: colors.textMuted,
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },
  filterTabActive: {
    background: 'rgba(47,182,92,0.20)',
    borderColor: 'rgba(47,182,92,0.55)',
    color: colors.parGreenBright
  },
  analyzeBtn: {
    background: colors.surface,
    border: `1px solid ${colors.glassBorder}`,
    color: colors.text,
    borderRadius: '8px',
    padding: '7px 14px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  enrichList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  enrichCard: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '10px',
    padding: '14px 16px'
  },
  enrichCardApproved: {
    borderLeft: '3px solid #2fb65c',
    background: 'rgba(47,182,92,0.10)'
  },
  enrichCardRejected: {
    borderLeft: '3px solid #ef4444',
    background: 'rgba(229,84,75,0.10)'
  },
  enrichHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  enrichSku: {
    fontWeight: '700',
    fontSize: '13px',
    color: colors.text,
    fontFamily: 'monospace'
  },
  enrichStatusBadge: {
    fontSize: '10px',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '8px',
    textTransform: 'uppercase'
  },
  badgePending: { background: 'rgba(240,168,48,0.18)', color: '#f0c68a' },
  badgeApproved: { background: 'rgba(47,182,92,0.18)', color: colors.parGreenBright },
  badgeRejected: { background: 'rgba(229,84,75,0.16)', color: '#f3b0aa' },
  enrichCurrent: {
    fontSize: '12px',
    color: colors.textMuted,
    marginBottom: '6px',
    lineHeight: '1.5'
  },
  enrichSuggestion: {
    fontSize: '13px',
    color: colors.text,
    marginBottom: '6px',
    lineHeight: '1.5'
  },
  enrichEvidence: {
    fontSize: '11px',
    color: colors.textFaint,
    fontStyle: 'italic',
    marginBottom: '10px',
    lineHeight: '1.5'
  },
  enrichSubLabel: {
    fontWeight: '600',
    color: colors.textMuted
  },
  enrichActions: { display: 'flex', gap: '8px', marginTop: '6px' },
  approveBtn: {
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 16px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },
  rejectBtn: {
    background: 'transparent',
    color: '#f3b0aa',
    border: '1px solid rgba(229,84,75,0.4)',
    borderRadius: '6px',
    padding: '5px 16px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  }
};
